import Checkbox from "@material-ui/core/Checkbox";
import withStyles from "@material-ui/core/styles/withStyles";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import { FormState } from "final-form";
import debounce from "lodash/debounce";
import get from "lodash/get";
import React from "react";
import {
  Field as FinalField,
  FieldProps,
  Form,
  FormRenderProps,
  FormSpy
} from "react-final-form";
import { Filter } from "../../dataAccess/DataSource";
import { LoggerFactory } from "../../logger";
import {
  Cell,
  Column,
  Columns,
  DataTableProps,
  EmptyCell,
  FilterCell,
  styles
} from "./commons";
import { DataTableToolbar } from "./DataTableToolbar";
import { TableHead, TableRow } from "@material-ui/core";
import { getTitle, LabelTitle } from "../DynamicForm/Label";

const log = LoggerFactory.create("DataTableHeader");

const DEFAULT_ORDER = "asc";

class DataTableHeaderComponent extends React.Component<DataTableProps> {
  private readonly debouncedOnFiltersChange: (state: FormState<any>) => void;

  public constructor(props: DataTableProps) {
    super(props);
    log.debug("props", this.props);
    this.debouncedOnFiltersChange = debounce(this.onFiltersChange, 500);
  }

  public render() {
    const { columns, classes, data, selected, params } = this.props;
    const indeterminate = data.length > selected.length && selected.length > 0;
    const checked = selected.length != 0 && selected.length === data.length;
    const initialValues = params.filters.reduce((acc, filter) => {
      const filterField = this.getFilterField(this.props.columns, filter.field);
      acc[filterField] = filter.value;
      return acc;
    }, {});

    return (
      <TableHead>
        <TableRow className={classes.header}>
          {!this.props.disableCheckboxes && (
            <Cell className={classes.menuCell}>
              <DataTableToolbar {...this.props} />
            </Cell>
          )}

          {columns.mapFields((column: Column, key: number) => {
            const sortField = this.getSortField(column);
            const sortHandler = this.sortHandlerCreator(sortField);
            const active = params.sort.sort === sortField;
            const direction = active ? params.sort.order : DEFAULT_ORDER;
            // for proper working, width should be in in % instead of px
            const style = { width: column.width };

            return column.sort !== false ? (
              <Cell
                key={`${column.name}-${key}`}
                className={classes.cell}
                style={style}
              >
                <TableSortLabel
                  direction={direction}
                  onClick={sortHandler}
                  active={active}
                >
                  <LabelTitle field={column} />
                </TableSortLabel>
              </Cell>
            ) : (
              <Cell
                key={`${column.name}-${key}`}
                style={{ width: column.width }}
              >
                {/*adding span for output html consistency with TableSortLabel*/}
                <span>
                  <LabelTitle field={column} />
                </span>
              </Cell>
            );
          })}
          <EmptyCell />
        </TableRow>
        {this.props.disableCheckboxes &&
        this.isRowContentEmpty(columns) ? null : (
          <TableRow>
            {!this.props.disableCheckboxes && (
              <Cell padding="checkbox" className={classes.checkboxCell}>
                <Checkbox
                  color="primary"
                  indeterminate={indeterminate}
                  checked={checked}
                  onChange={this.toggleAll}
                />
              </Cell>
            )}
            <Form
              onSubmit={this.submit}
              render={this.filters}
              initialValues={initialValues}
            />
            <EmptyCell />
          </TableRow>
        )}
      </TableHead>
    );
  }

  private submit() {
    return;
  }

  private getSortField = (column: Column): string => {
    return get(column, "sort.field", column.name);
  };

  private getFilterField = (columns: Columns | Column, key: string): string => {
    const column = columns instanceof Columns ? columns.Named[key] : columns;
    return get(column, "filter.field", key);
  };

  private sortHandlerCreator = (columnName: string) => (...args: any[]) => {
    log.debug("changeSortOrder", args, columnName);

    const { params } = this.props;
    const active = params.sort.sort === columnName;

    const currentOrder = params.sort.order;
    const reversedOrder = currentOrder === "asc" ? "desc" : "asc";
    const newOrder = active ? reversedOrder : DEFAULT_ORDER;
    this.props.search({
      ...params,
      sort: { sort: columnName, order: newOrder }
    });
  };

  private filters = (formRenderProps: FormRenderProps) => {
    const { columns } = this.props;

    return (
      <>
        <FormSpy onChange={this.debouncedOnFiltersChange} />
        {columns.mapFields(column => {
          const componentProps = this.prepareComponentProps(column);

          return column.filter !== false ? (
            <FilterCell key={column.name}>
              <FinalField {...componentProps} />
            </FilterCell>
          ) : (
            <EmptyCell key={column.name} />
          );
        })}
      </>
    );
  };

  private isRowContentEmpty = (columns: Columns) => {
    let isEmpty = true;
    {
      columns.mapFields((column: Column) => {
        if (column.filter !== false) {
          isEmpty = false;
        }
      });
    }
    return isEmpty;
  };

  private onFiltersChange = (formState: FormState<any>) => {
    const { columns } = this.props;
    const { sort, page } = this.props.params;

    if (formState.pristine) {
      return;
    }
    this.props.search({
      filters: this.mapToArr(formState.values, columns),
      sort,
      page: {
        pageNumber: 0,
        pageSize: page.pageSize
      }
    });
  };

  private toggleAll = (...args: any[]) => {
    this.props.toggleSelectAll();
    return;
  };

  private prepareComponentProps(column: Column) {
    return {
      placeholder: getTitle(column),
      name: this.getFilterField(column, column.name),
      meta: {},
      key: column.name,
      variant: "outlined",
      margin: "dense",
      type: "search",
      component: column.type.Component,
      ...column.type.props,
      clearable: true
    } as FieldProps<any, any>;
  }

  private mapToArr(formStateValues: {}, columns: Columns): Filter[] {
    return Object.keys(formStateValues).reduce((acc, key) => {
      const filterField = this.getFilterField(columns, key);
      acc.push({
        field: filterField,
        value: formStateValues[key]
      });
      return acc;
    }, [] as Filter[]);
  }
}

export const DataTableHeader = withStyles(styles)(DataTableHeaderComponent);
